/**
 * A costura entre o SDK e a plataforma que o hospeda.
 *
 * O SDK é o mesmo em três sítios — browser, app de desktop e telemóvel — e as
 * diferenças entre eles cabem todas aqui. Tudo o resto (REST, gateway, store,
 * voz) não sabe em que plataforma está a correr, e é isso que torna o
 * reaproveitamento real em vez de teórico.
 *
 * As três diferenças, e porque são diferenças:
 *
 *  - **Origem.** Na web o servidor é a própria página. Numa app instalada a
 *    página vem do bundle local, logo um caminho relativo nunca sairia da
 *    máquina.
 *  - **Refresh token.** Um cookie `HttpOnly` é a resposta certa num browser —
 *    é o que o põe fora do alcance de XSS. Numa app instalada não há cookie
 *    jar que sobreviva e o equivalente é o cofre do sistema.
 *  - **WebSocket.** O browser e o React Native trazem o seu; são compatíveis
 *    no que usamos, mas quem hospeda tem de poder substituí-lo.
 */

/**
 * Cofre do sistema para o refresh token.
 *
 * `null` significa "esta plataforma usa cookies" — e não "não guarda nada":
 * o browser guarda, só que o JS nunca lhe toca.
 */
export interface SecretStore {
  get(): Promise<string | null>;
  set(token: string): Promise<void>;
  clear(): Promise<void>;
}

export interface SdkConfig {
  /** `https://host`, sem barra final. */
  origin: string;
  /** `null` = o refresh viaja em cookie, como num browser. */
  secrets: SecretStore | null;
  /** Fábrica de WebSocket, para quem não tem um global utilizável. */
  websocket: (url: string) => WebSocket;
}

let current: SdkConfig | null = null;

/**
 * Chamado UMA vez, antes de qualquer pedido.
 *
 * Deliberadamente um módulo global e não um objecto passado de mão em mão:
 * a alternativa era enfiar um contexto em cada assinatura do SDK — incluindo
 * nas funções que o store chama — para suportar uma pluralidade de
 * configurações que nenhuma das três apps tem. Uma app fala com um servidor.
 */
export function configure(config: Partial<SdkConfig> & { origin: string }): void {
  current = {
    origin: config.origin.replace(/\/$/, ''),
    secrets: config.secrets ?? null,
    websocket: config.websocket ?? ((url) => new WebSocket(url)),
  };
}

export function sdk(): SdkConfig {
  if (!current) {
    // Falhar alto. Sem configuração, o SDK chamaria `undefined/api/v1` e o
    // erro apareceria como um 404 inexplicável três camadas acima.
    throw new Error('@tasca/client-sdk: configure() tem de ser chamado no arranque');
  }
  return current;
}

/** `https://host` → `wss://host`. */
export function wsOrigin(): string {
  return sdk().origin.replace(/^http/, 'ws');
}

/** A plataforma guarda o refresh token ela própria? */
export function isNativeClient(): boolean {
  return sdk().secrets !== null;
}
